<template>
    <CRow>
        <CCol col="12">
            <CCard>
                <CCardHeader>
                    <h3 class="float-left">Order List</h3>
                    <!-- Button to Go Back -->
                    <CButton
                        class="float-right hideOnPrint"
                        color="secondary"
                        @click="$router.go(-1)"
                        ><CIcon name="cilChevronLeft" />
                        Back
                    </CButton>
                </CCardHeader>
                <CCardBody v-if="items">
                    <CRow>
                        <CCol md="3">
                            <label>From Date</label>
                            <datepicker
                                v-model="fromDate"
                                :format="dateFormat"
                            ></datepicker>
                        </CCol>
                        <CCol md="3">
                            <label>To Date</label>
                            <datepicker
                                v-model="toDate"
                                :format="dateFormat"
                            ></datepicker>
                        </CCol>
                        <CCol md="1">
                            <CButton
                                class="filterBtn"
                                type="submit"
                                color="primary"
                                @click="filterData"
                            >
                                Filter</CButton
                            >
                        </CCol>
                        <CCol md="5">
                            <CAlert color="warning m-2" v-if="limitReached">
                                Warning! Your filter has reached the limit of
                                {{ limit }} orders. Please alter your filters to
                                see other results.
                            </CAlert>
                        </CCol>
                    </CRow>
                    <CDataTable
                        hover
                        striped
                        clickable-rows
                        :sorter="{ external: false, resetable: true }"
                        :items="items"
                        :fields="fields"
                        :items-per-page="pageLimit"
                        :itemsPerPageSelect="true"
                        :columnFilter="{ external: false, lazy: false }"
                        :active-page="activePage"
                        @row-clicked="rowClicked"
                        @pagination-change="paginationChanged"
                    >
                        <template #over-table>
                            <CButton
                                v-if="hasPayInvoicePermission"
                                color="success"
                                class="px-4 mb-1"
                                @click="payInvoices"
                                type="submit"
                                :disabled="selectedInvoiceNumbers.length == 0"
                                ><CIcon name="cil-credit-card" />
                                Pay Selected Invoices <span v-if="selectedInvoiceNumbers.length > 0">({{ selectedInvoiceNumbers.length }})</span>
                            </CButton>
                            <CButton
                                class="float-right px-4  mb-1"
                                color="primary"
                            >
                                <download-csv :data="items">
                                    <CIcon name="cil-save" />
                                    Download Data
                                </download-csv>
                            </CButton>
                        </template>
                        <template #status="data">
                            <td>
                                <CBadge :color="getBadge(data.item.status)">
                                    {{ data.item.status }}
                                </CBadge>
                            </td>
                        </template>
                        <template #orderTotal="data">
                            <td class="text-right">{{ data.item.orderTotal | currency }}</td>
                        </template>
                        <template #action="data">
                            <td class="py-2">
                                <CInputCheckbox
                                    v-if="hasPayInvoicePermission"
                                    :id="'order-checkbox-' + data.item.orderNumber"
                                    :checked="selectedOrderNumbers.indexOf( parseInt(data.item.orderNumber) ) !== -1"
                                    @click.stop="return false"
                                    @change="orderClicked(data.item)"
                                />
                            </td>
                        </template>
                        <template v-if="hasPayInvoicePermission" #action-header>
                            <CInputCheckbox
                                @click.stop="return false"
                                @change="selectAllClicked($event)"
                            />
                        </template>
                    </CDataTable>

                    <DataTablePagination
                        :activePage="activePage"
                        :total="items && items.length || 0"
                        :limit="pageLimit"
                        @page-changed="pageChanged"
                    />
                </CCardBody>
            </CCard>
        </CCol>
    </CRow>
</template>

<script>
/*
 * OrderList Vue.js view module for the order list history for Suncast
 * Display the history of previous orders for the logged in client
 * @module OrderList
 * @author Mithun Paulose (Fresche Solutions)
 * @date June 2020
 */
import { mapState, mapMutations, mapActions, mapGetters } from "vuex";
import { mapFields } from "vuex-map-fields";    // Two-way mapping of local state fields to vuex
import Datepicker from "vuejs-datepicker";
import JsonCSV from "vue-json-csv";
import { authService } from "../../../../services/authService";
import DataTablePagination from "../components/DataTablePagination";

export default {
    name: "OrderList",
    components: {
        downloadCsv: JsonCSV,
        Datepicker,
        DataTablePagination,
    },
    created: function() {
        this.init();
    },
    data() {
        return {
            // Treat as constants
            STATUS_PAID: 'Paid',
            STATUS_PROCESSING: 'Processing',

            dateFormat: "yyyy-MM-dd",
            fromDate: null,
            toDate: null,
            limitReached: false,
            limit: null,
            selectedOrderNumbers: [],
            selectedInvoiceNumbers: [],
            openInvoices: [],
            pageLimit: 10,
            items: null,
            activePage: 1,
            details: [],
            collapseDuration: 0,

            hasPayInvoicePermission: false,

            fields: [
                {
                    key: "action",
                    label: "",
                    _style: "width:1%",
                    sorter: false,
                    filter: false
                },
                {
                    key: "orderNumber",
                    label: "Order Number"
                },
                {
                    key: "customerPO",
                    label: "Customer PO Number"
                },
                {
                    key: "orderDate",
                    label: "Order Date"
                },
                {
                    key: "status",
                    label: "Status"
                },
                {
                    key: "orderTotal",
                    label: "Order Total"
                },
                {
                    key: "promiseDate",
                    label: "Promise Date"
                },
                {
                    key: "shipDate",
                    label: "Ship Date"
                }
            ]
        };
    },
    watch: {
        $route: {
            immediate: true,
            handler(route) {
                if (route.query && route.query.page) {
                    this.activePage = Number(route.query.page);
                }
            }
        }
    },
    mounted() {
        this.pageLimit = this.getPageLimit();
    },
    computed: {
        ...authService,
    },
    methods: {
        ...mapMutations("OrderHistory", ["setFromDate", "setToDate", "setPageLimit"]),
        ...mapActions("OrderHistory", ["fetchOrderList", "fetchOrderDetails"]),
        ...mapGetters("OrderHistory", ["getOrderList", "getOrderDetails", "getFromDate", "getToDate", "getPageLimit"]),
        ...mapGetters("User", ["getSelectedCustomer"]),
        ...mapGetters("Invoice", ["getSelectedInvoices", "getOpenInvoices"]),
        ...mapActions("Invoice", ["updateSelectedInvoices", "fetchOpenInvoiceList"]),
        filterData() {
            this.setFromDate(this.fromDate);
            this.setToDate(this.toDate);
            // get the order data
            this.getOrders(true);
        },
        async init() {
            // Update the redirect from route, if provided
            if (this.$route.params.redirect) {
                this.redirectTo = this.$route.params.redirect;
            }

            // get previously selected invoices from the store
            this.selectedInvoiceNumbers = this.getSelectedInvoices();

            // get last used date filters from store
            this.fromDate = this.getFromDate();
            this.toDate = this.getToDate();

            // if the dates are null, set default
            if (this.fromDate == null || this.toDate == null) {
                // set default date filters
                this.fromDate = new Date();
                this.fromDate.setMonth(this.fromDate.getMonth() - 1);
                this.toDate = new Date();

                this.setFromDate(this.fromDate);
                this.setToDate(this.toDate);
            }

            // get the order data
            this.getOrders(false);

            // Get all open invoices
            let openInvoiceData = this.getOpenInvoices();
            if (!openInvoiceData || openInvoiceData.length == 0) {
                await this.fetchOpenInvoiceList();
                openInvoiceData = this.getOpenInvoices();
            }
            this.openInvoices = openInvoiceData.invoices;

            // Determine relevant user permissions
            this.hasPayInvoicePermission = authService.userAuthResourcePriv('Invoices', 'all');
        },

        async getOrders(fetchNew) {
            let orderList = this.getOrderList();

            // only fetch orders on filter, or if we haven't yet
            if (
                fetchNew || // filtering
                orderList == null || // have no orders
                orderList.orders.length == 0 || // orders are empty
                orderList.orders[0].customerNumber !== this.getSelectedCustomer().customerId // orders are for a different customer
            ) {
                // Get the order list
                await this.fetchOrderList({
                    fromDate: this.fromDate,
                    toDate: this.toDate
                });
                orderList = this.getOrderList();
            }

            // If this user has no previous orders
            if (orderList.length == 0) {
                this.limitReached = false;
            }
            // Order list is non empty
            else {
                this.items = orderList.orders;
                this.limitReached = orderList.limitReached;
                this.limit = orderList.limit;
            }
        },
        getBadge(status) {
            switch (status) {
                case "A-Active":
                    return "success";
                case "C-Shipped":
                    return "info";
                case "B-Backordered":
                    return "warning";
                case "X-Cancelled":
                    return "danger";
                default:
                    "primary";
            }
        },

        // Handles an order's checkbox being checked/unchecked
        async orderClicked(item) {

            let checkboxEl = this.getCheckboxElement(item.orderNumber);
            let action = (checkboxEl.checked ? 'add' : 'delete');
            let success = true;

            // Attempt to add/remove the order & its invoice(s). False on add/remove order simply
            // means the order is already added/removed so there's nothing to do.
            if (this.addRemoveOrder(item.orderNumber, action)) {
                success = this.addRemoveOpenOrderInvoices(item.orderNumber, action);
            }

            // If failed invoice add, there are no invoices to pay - display a message
            if (action == 'add' && !success) {

                // Uncheck the checkbox. Don't manipulate the element directly as it will apply to the
                // checkbox in that row, even after sorting.
                checkboxEl.click();

                this.$toast.open({
                    message: 'There are no invoices in this order that require payment',
                    type: "info"
                });
            }
        },

        // Select/Deselect all orders
        async selectAllClicked(event) {

            // Unchecking - clear arrays
            if (!event.target.checked) {
                this.selectedOrderNumbers = [];
                this.selectedInvoiceNumbers = [];
                return;
            }

            // Selecting an order - attempt to add each order to the selected orders array, along with
            // each one's invoice numbers.
            var errorCount = 0;
            for (let i = 0; i < this.items.length; i++) {

                let orderNumber = this.items[i].orderNumber;

                // Attempt to add/remove the order & its invoice(s). False on add/remove order simply
                // means the order is already added/removed so there's nothing to do.
                let success = true;
                if (this.addRemoveOrder(orderNumber, 'add')) {
                    success = this.addRemoveOpenOrderInvoices(orderNumber, 'add');
                }

                // Error - increment the count
                if (!success) {
                    errorCount++;
                    continue;
                }

                // Successful - if there's a checkbox for it on the current page and it's unchecked, check it
                let checkboxEl = this.getCheckboxElement(orderNumber);
                if (checkboxEl && !checkboxEl.checked) {
                    checkboxEl.click();
                }
            }

            // Display a popup indiciation if any orders had no invoices requiring payment
            if (errorCount > 0) {
                this.$toast.open({
                    message: 'There ' + (errorCount == 1 ? ' is 1 order' : ' are ' + errorCount + ' orders') + ' that do not require payment',
                    type: "info"
                });
            }
        },

        // Add/Remove an order number to/from the order number list
        // @return bool - true if added/removed, false if the order has already been added/removed
        addRemoveOrder(orderNumber, action) {

            // Get the order's index in the selected orders list if it's there
            orderNumber = parseInt(orderNumber);
            let pos = this.selectedOrderNumbers.indexOf(orderNumber);

            // Add the order number if it isn't already there
            if (action == 'add' && pos == -1) {
                this.selectedOrderNumbers.push(orderNumber);
            }
            // Delete the order number if present
            else if (action == 'delete' && pos != -1) {
                this.selectedOrderNumbers.splice(pos, 1);
            }
            // Either adding but it's already in the list or removing an order that isn't
            else {
                return false;
            }

            return true;
        },

        // Add/Remove each invoice number contained in the specified order
        addRemoveOpenOrderInvoices(orderNumber, action) {

            // Get the open invoices for this order
            let invoices = this.openInvoices.filter(item => item.orderNumber == orderNumber);
            if (invoices.length == 0) {
                return false;
            }

            // map each items inside the order details.
            invoices.map(item => {
                item.invoiceNumber = parseInt(item.invoiceNumber);
                let pos = this.selectedInvoiceNumbers.indexOf(item.invoiceNumber);

                // If adding and the invoice number is new to this.selectedInvoiceNumbers, push it
                if (action == 'add' && pos == -1) {
                    this.selectedInvoiceNumbers.push(item.invoiceNumber);
                }
                // Delete the invoice number if present
                else if (action == 'delete' && pos !== -1) {
                    this.selectedInvoiceNumbers.splice(pos, 1);
                }
            });

            return true;
        },

        // Row clicked - go to its details page
        rowClicked(order) {
            this.$router.push({ path: `/orders/display/${order.orderNumber}` });
        },

        // Get the checkbox element by order number
        getCheckboxElement(orderNumber) {
            return document.getElementById('order-checkbox-' + orderNumber);
        },

        //navigate to pay invoices
        async payInvoices() {
            await this.updateSelectedInvoices(this.selectedInvoiceNumbers);
            this.$router.push({ path: "/invoices/payInvoices" });
        },

        // Change & save the page limit
        paginationChanged(pageLimit) {
            this.setPageLimit(pageLimit);
            this.pageLimit = pageLimit;
        },

        // Data table page change
        pageChanged(newPage) {

            // Get the page we're currently on
            let query = this.$router.currentRoute.query;

            // If it's different, navigate & set the active page
            if (!query.page || query.page != newPage) {
                this.activePage = newPage;
                this.$router.push({ query: { page: newPage } });
            }
        },
    }
};
</script>
<style scoped>
    .filterBtn {
        margin-top: 25px;
    }
</style>
